import React, { useMemo, useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  CheckCircle2,
  MessageSquare,
  MoveRight,
  UserCog,
  ChevronDown,
  ChevronRight,
  Building2,
  MoreHorizontal,
  Trash2,
  UserPlus,
  GripVertical,
  XCircle,
  RotateCcw,
  Ticket,
} from "lucide-react";
import GravatarAvatar from "@/components/GravatarAvatar/GravatarAvatar";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import { EventEnrollmentItemResponseDTO } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchEventEnrollments/types";
import { EventWaitlistItemResponse } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchEventWaitlistItems/types";
import { WaitlistDetails } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchWaitlistDetails/types";
import { RegisteredUser } from "@/modules/eventRegistration/features/EventWaitlist/hooks/useWaitlist";

export type RosterFilter = "all" | "registered" | "waitlisted" | "cancelled";

interface RosterTabProps {
  rosterFilter: RosterFilter;
  registeredUsers: EventEnrollmentItemResponseDTO[];
  waitlist: EventWaitlistItemResponse[];
  waitlistDetails: WaitlistDetails | null;
  isSessionFull: boolean;
  formatDate: (dateString: string) => string;
  handleReplaceClick: (registration: RegisteredUser) => void;
  handleMoveToWaitlistClick: (registration: RegisteredUser) => void;
  handleRegisterClick: (user: EventWaitlistItemResponse) => void;
  handleRemoveClick: (user: EventWaitlistItemResponse) => void;
  handleCancelClick: (registration: RegisteredUser) => void;
  handleReinstateClick: (registration: RegisteredUser) => void;
  loading?: boolean;
  onPositionChange: (waitlistId: number, newPosition: number) => Promise<void>;
  onMoveToTop: (waitlistId: number) => Promise<void>;
}

interface CompanyGroup<T> {
  companyName: string;
  items: T[];
}

function groupByCompany<T extends { companyName?: string | null }>(
  items: T[],
): { groups: CompanyGroup<T>[]; ungrouped: T[] } {
  const map = new Map<string, T[]>();
  const ungrouped: T[] = [];

  for (const item of items) {
    const company = item.companyName?.trim();
    if (!company) {
      ungrouped.push(item);
      continue;
    }
    if (!map.has(company)) {
      map.set(company, []);
    }
    map.get(company)!.push(item);
  }

  const groups: CompanyGroup<T>[] = [];
  map.forEach((companyItems, companyName) => {
    groups.push({ companyName, items: companyItems });
  });

  groups.sort((a, b) => a.companyName.localeCompare(b.companyName));

  return { groups, ungrouped };
}

// --- Shared sub-components ---

function CompanyHeader({
  companyName,
  count,
  isCollapsed,
  onToggle,
}: {
  companyName: string;
  count: number;
  isCollapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      className="flex items-center gap-1.5 py-2 px-1 w-full text-left"
      onClick={onToggle}
      type="button"
    >
      {isCollapsed ? (
        <ChevronRight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
      ) : (
        <ChevronDown className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
      )}
      <Building2 className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {companyName}
      </span>
      <span className="text-xs text-gray-400 dark:text-gray-500">
        ({count})
      </span>
    </button>
  );
}

function SpecialRequestsPopover({ text }: { text: string }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          title="Special requests"
          type="button"
        >
          <MessageSquare className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" side="top">
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Special Requests
          </p>
          <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
            {text}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function OverflowMenu({ children }: { children: React.ReactNode }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded"
          type="button"
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-44 p-1" side="bottom">
        {children}
      </PopoverContent>
    </Popover>
  );
}

function OverflowMenuItem({
  icon,
  label,
  onClick,
  variant = "default",
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  variant?: "default" | "danger";
}) {
  return (
    <button
      className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded transition-colors text-left ${
        variant === "danger"
          ? "text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20"
          : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800"
      }`}
      onClick={onClick}
      type="button"
    >
      {icon}
      {label}
    </button>
  );
}

/**
 * Renders the parent EventCheckout's contact-person info inline below the
 * enrollee details. Admins use this to call/email the registrant directly
 * without drilling into the checkout record. Renders nothing when no
 * checkout-level info is present (legacy rows).
 */
function ContactInfoBlock({
  contactName,
  contactEmail,
  contactPhone,
  groupNotes,
}: {
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  groupNotes: string | null;
}) {
  if (!contactName && !contactEmail && !contactPhone && !groupNotes) {
    return null;
  }

  return (
    <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 space-y-0.5">
      <div className="flex flex-wrap items-center gap-x-2">
        <span className="font-medium text-gray-600 dark:text-gray-300">
          Contact:
        </span>
        {contactName && <span>{contactName}</span>}
        {contactEmail && (
          <a
            className="hover:underline"
            href={`mailto:${contactEmail}`}
            onClick={(e) => e.stopPropagation()}
          >
            {contactEmail}
          </a>
        )}
        {contactPhone && (
          <a
            className="hover:underline"
            href={`tel:${contactPhone}`}
            onClick={(e) => e.stopPropagation()}
          >
            {contactPhone}
          </a>
        )}
      </div>
      {groupNotes && (
        <div className="italic text-gray-400 dark:text-gray-500 line-clamp-2">
          Notes: {groupNotes}
        </div>
      )}
    </div>
  );
}

// --- Registered person row ---

function RegisteredPersonRow({
  user,
  isSessionFinalized,
  handleReplaceClick,
  handleMoveToWaitlistClick,
  handleCancelClick,
}: {
  user: EventEnrollmentItemResponseDTO;
  isSessionFinalized?: boolean;
  handleReplaceClick: (registration: RegisteredUser) => void;
  handleMoveToWaitlistClick: (registration: RegisteredUser) => void;
  handleCancelClick: (registration: RegisteredUser) => void;
}) {
  return (
    <div className="flex items-center justify-between py-2.5 px-3 border-b border-gray-100 dark:border-slate-700/50 last:border-b-0">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <GravatarAvatar
          email={user.email}
          firstName={user.firstName}
          lastName={user.lastName}
          size="sm"
        />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {user.firstName} {user.lastName}
            </p>
            {user.usedVoucher && (
              <Ticket className="h-3.5 w-3.5 text-green-500 dark:text-green-400 flex-shrink-0" />
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {user.email}
          </p>
          {user.enrolledAt && (
            <p className="text-[11px] text-gray-400 dark:text-gray-500">
              Enrolled {new Date(user.enrolledAt).toLocaleDateString()}
            </p>
          )}
          {user.specialRequests && (
            <p className="text-xs mt-1 line-clamp-2">
              <span className="font-medium text-amber-600 dark:text-amber-400">
                Special Request:
              </span>{" "}
              <span className="text-amber-700 dark:text-amber-400/80">
                {user.specialRequests}
              </span>
            </p>
          )}
          <ContactInfoBlock
            contactName={user.contactName}
            contactEmail={user.contactEmail}
            contactPhone={user.contactPhone}
            groupNotes={user.groupNotes}
          />
        </div>
      </div>
      {!isSessionFinalized && (
        <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
          <Button
            className="h-7 text-xs"
            onClick={() => handleReplaceClick(user)}
            size="sm"
            variant="ghost"
          >
            <UserCog className="h-3.5 w-3.5 mr-1" />
            Replace
          </Button>
          <OverflowMenu>
            <OverflowMenuItem
              icon={<MoveRight className="h-3.5 w-3.5" />}
              label="Move to Waitlist"
              onClick={() => handleMoveToWaitlistClick(user)}
            />
            <OverflowMenuItem
              icon={<XCircle className="h-3.5 w-3.5" />}
              label="Cancel Enrollment"
              onClick={() => handleCancelClick(user)}
              variant="danger"
            />
          </OverflowMenu>
        </div>
      )}
    </div>
  );
}

// --- Waitlist person row (sortable) ---

function WaitlistPersonRow({
  user,
  position,
  isSessionFinalized,
  isSessionFull,
  handleRegisterClick,
  handleRemoveClick,
  onMoveToTop,
}: {
  user: EventWaitlistItemResponse;
  position: number;
  isSessionFinalized?: boolean;
  isSessionFull: boolean;
  handleRegisterClick: (user: EventWaitlistItemResponse) => void;
  handleRemoveClick: (user: EventWaitlistItemResponse) => void;
  onMoveToTop: (waitlistId: number) => Promise<void>;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: user.id,
    disabled: !!isSessionFinalized,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      className="flex items-center justify-between py-2.5 px-3 border-b border-gray-100 dark:border-slate-700/50 last:border-b-0 bg-white dark:bg-slate-800"
      ref={setNodeRef}
      style={style}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {!isSessionFinalized && (
          <button
            className="cursor-grab active:cursor-grabbing text-gray-300 dark:text-gray-600 hover:text-gray-400 dark:hover:text-gray-500 flex-shrink-0 touch-none"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
        )}
        <span className="text-xs font-medium text-gray-400 dark:text-gray-500 w-5 text-center flex-shrink-0">
          {position}
        </span>
        <GravatarAvatar
          email={user.email}
          firstName={user.firstName}
          lastName={user.lastName}
          size="sm"
        />
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {user.firstName} {user.lastName}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {[user.email, user.companyName].filter(Boolean).join(" · ")}
          </p>
          {user.specialRequests && (
            <p className="text-xs mt-1 line-clamp-2">
              <span className="font-medium text-amber-600 dark:text-amber-400">
                Special Request:
              </span>{" "}
              <span className="text-amber-700 dark:text-amber-400/80">
                {user.specialRequests}
              </span>
            </p>
          )}
          <ContactInfoBlock
            contactName={user.contactName}
            contactEmail={user.contactEmail}
            contactPhone={user.contactPhone}
            groupNotes={user.groupNotes}
          />
        </div>
      </div>
      {!isSessionFinalized && (
        <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
          <Button
            className="h-7 text-xs"
            onClick={() => handleRegisterClick(user)}
            size="sm"
            variant="ghost"
          >
            <UserPlus className="h-3.5 w-3.5 mr-1" />
            Enroll
          </Button>
          <OverflowMenu>
            <OverflowMenuItem
              icon={<Trash2 className="h-3.5 w-3.5" />}
              label="Remove"
              onClick={() => handleRemoveClick(user)}
              variant="danger"
            />
          </OverflowMenu>
        </div>
      )}
    </div>
  );
}

// --- Cancelled person row ---

function CancelledPersonRow({
  user,
  formatDate,
  isSessionFinalized,
  handleReinstateClick,
}: {
  user: EventEnrollmentItemResponseDTO;
  formatDate: (dateString: string) => string;
  isSessionFinalized?: boolean;
  handleReinstateClick: (registration: RegisteredUser) => void;
}) {
  return (
    <div className="flex items-center justify-between py-2.5 px-3 border-b border-gray-100 dark:border-slate-700/50 last:border-b-0">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <GravatarAvatar
          email={user.email}
          firstName={user.firstName}
          lastName={user.lastName}
          size="sm"
        />
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {user.firstName} {user.lastName}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {[
              user.email,
              user.cancellationReason
                ? `Reason: ${user.cancellationReason}`
                : null,
              user.cancelledAt ? formatDate(user.cancelledAt) : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
          <ContactInfoBlock
            contactName={user.contactName}
            contactEmail={user.contactEmail}
            contactPhone={user.contactPhone}
            groupNotes={user.groupNotes}
          />
        </div>
      </div>
      {!isSessionFinalized && (
        <Button
          className="h-7 text-xs flex-shrink-0 ml-3"
          onClick={() => handleReinstateClick(user)}
          size="sm"
          variant="ghost"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1" />
          Reinstate
        </Button>
      )}
    </div>
  );
}

// --- Waitlist section with drag-to-reorder ---

function WaitlistSection({
  waitlist,
  rosterFilter,
  isSessionFinalized,
  isSessionFull,
  handleRegisterClick,
  handleRemoveClick,
  onMoveToTop,
  onPositionChange,
  waitlistPositions,
}: {
  waitlist: EventWaitlistItemResponse[];
  rosterFilter: RosterFilter;
  isSessionFinalized?: boolean;
  isSessionFull: boolean;
  handleRegisterClick: (user: EventWaitlistItemResponse) => void;
  handleRemoveClick: (user: EventWaitlistItemResponse) => void;
  onMoveToTop: (waitlistId: number) => Promise<void>;
  onPositionChange: (waitlistId: number, newPosition: number) => Promise<void>;
  waitlistPositions: Map<number, number>;
}) {
  const [items, setItems] = useState(waitlist);

  useEffect(() => {
    setItems(waitlist);
  }, [waitlist]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);

    const newItems = arrayMove(items, oldIndex, newIndex);
    setItems(newItems);

    try {
      await onPositionChange(Number(active.id), newIndex + 1);
    } catch {
      setItems(waitlist);
    }
  };

  return (
    <div className="mb-8">
      {rosterFilter === "all" && (
        <h3 className="text-base font-normal text-gray-900 dark:text-white mb-3">
          Waitlisted ({waitlist.length})
        </h3>
      )}
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <SortableContext
          items={items.map((i) => i.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="ml-5 border border-gray-100 dark:border-slate-700 rounded-md overflow-hidden">
            {items.map((user, idx) => (
              <WaitlistPersonRow
                handleRegisterClick={handleRegisterClick}
                handleRemoveClick={handleRemoveClick}
                isSessionFinalized={isSessionFinalized}
                isSessionFull={isSessionFull}
                key={user.id}
                onMoveToTop={onMoveToTop}
                position={idx + 1}
                user={user}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

// --- Main component ---

export default function RosterTab({
  rosterFilter,
  registeredUsers,
  waitlist,
  waitlistDetails,
  isSessionFull,
  formatDate,
  handleReplaceClick,
  handleMoveToWaitlistClick,
  handleRegisterClick,
  handleRemoveClick,
  handleCancelClick,
  handleReinstateClick,
  loading = false,
  onPositionChange,
  onMoveToTop,
}: RosterTabProps) {
  const isSessionFinalized = waitlistDetails?.isSessionFinalized;
  const [collapsedCompanies, setCollapsedCompanies] = useState<Set<string>>(
    new Set(),
  );

  const activeUsers = registeredUsers.filter((u) => !u.isCancelled);
  const cancelledUsers = registeredUsers.filter((u) => u.isCancelled);

  const showRegistered =
    rosterFilter === "all" || rosterFilter === "registered";
  const showWaitlisted =
    rosterFilter === "all" || rosterFilter === "waitlisted";
  const showCancelled = rosterFilter === "all" || rosterFilter === "cancelled";
  const registeredGrouped = useMemo(
    () => groupByCompany(activeUsers),
    [activeUsers],
  );
  const waitlistGrouped = useMemo(() => groupByCompany(waitlist), [waitlist]);
  const cancelledGrouped = useMemo(
    () => groupByCompany(cancelledUsers),
    [cancelledUsers],
  );

  // Build unified company view across all statuses
  const companySummaries = useMemo(() => {
    const map = new Map<
      string,
      {
        registered: EventEnrollmentItemResponseDTO[];
        waitlisted: EventWaitlistItemResponse[];
        cancelled: EventEnrollmentItemResponseDTO[];
      }
    >();

    activeUsers.forEach((u) => {
      const name = u.companyName?.trim();
      if (!name) return;
      if (!map.has(name))
        map.set(name, { registered: [], waitlisted: [], cancelled: [] });
      map.get(name)!.registered.push(u);
    });

    waitlist.forEach((u) => {
      const name = u.companyName?.trim();
      if (!name) return;
      if (!map.has(name))
        map.set(name, { registered: [], waitlisted: [], cancelled: [] });
      map.get(name)!.waitlisted.push(u);
    });

    cancelledUsers.forEach((u) => {
      const name = u.companyName?.trim();
      if (!name) return;
      if (!map.has(name))
        map.set(name, { registered: [], waitlisted: [], cancelled: [] });
      map.get(name)!.cancelled.push(u);
    });

    const result: {
      companyName: string;
      registered: EventEnrollmentItemResponseDTO[];
      waitlisted: EventWaitlistItemResponse[];
      cancelled: EventEnrollmentItemResponseDTO[];
      total: number;
    }[] = [];

    map.forEach((data, companyName) => {
      result.push({
        companyName,
        ...data,
        total:
          data.registered.length +
          data.waitlisted.length +
          data.cancelled.length,
      });
    });

    result.sort((a, b) => a.companyName.localeCompare(b.companyName));
    return result;
  }, [activeUsers, waitlist, cancelledUsers]);

  const toggleCompany = (key: string) => {
    setCollapsedCompanies((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Build a position lookup for waitlist items (they come pre-sorted)
  const waitlistPositions = useMemo(() => {
    const map = new Map<number, number>();
    waitlist.forEach((item, idx) => {
      map.set(item.id, item.waitlistPosition ?? idx + 1);
    });
    return map;
  }, [waitlist]);

  const renderCompanySection = <T extends { companyName?: string | null }>(
    groups: CompanyGroup<T>[],
    ungrouped: T[],
    prefix: string,
    renderItem: (item: T) => React.ReactNode,
    getKey: (item: T) => number | string,
  ) => (
    <div className="space-y-1">
      {groups.map((group) => {
        const key = `${prefix}:${group.companyName}`;
        const isCollapsed = collapsedCompanies.has(key);

        return (
          <div key={group.companyName}>
            <CompanyHeader
              companyName={group.companyName}
              count={group.items.length}
              isCollapsed={isCollapsed}
              onToggle={() => toggleCompany(key)}
            />
            {!isCollapsed && (
              <div className="ml-5 border border-gray-100 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800">
                {group.items.map((item) => (
                  <React.Fragment key={getKey(item)}>
                    {renderItem(item)}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {ungrouped.length > 0 && (
        <div className="border border-gray-100 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800">
          {ungrouped.map((item) => (
            <React.Fragment key={getKey(item)}>
              {renderItem(item)}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div>
      {isSessionFinalized && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-6 h-6 bg-green-100 dark:bg-green-800/30 rounded-full">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                Session Finalized
              </p>
              <p className="text-sm text-green-700 dark:text-green-300">
                This session has been finalized. Roster records are now
                read-only.
              </p>
            </div>
          </div>
        </div>
      )}

      {activeUsers.some((u) => u.usedVoucher) && (
        <div className="mb-3 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <Ticket className="h-3.5 w-3.5 text-green-500 dark:text-green-400" />
          <span>= Voucher attendee</span>
        </div>
      )}

      {showRegistered && activeUsers.length > 0 && (
        <div className="mb-8">
          {rosterFilter === "all" && (
            <h3 className="text-base font-normal text-gray-900 dark:text-white mb-3">
              Registered ({activeUsers.length})
            </h3>
          )}
          {renderCompanySection(
            registeredGrouped.groups,
            registeredGrouped.ungrouped,
            "registered",
            (user: EventEnrollmentItemResponseDTO) => (
              <RegisteredPersonRow
                handleCancelClick={handleCancelClick}
                handleMoveToWaitlistClick={handleMoveToWaitlistClick}
                handleReplaceClick={handleReplaceClick}
                isSessionFinalized={isSessionFinalized}
                user={user}
              />
            ),
            (user) => user.id,
          )}
        </div>
      )}

      {showWaitlisted && waitlist.length > 0 && (
        <WaitlistSection
          handleRegisterClick={handleRegisterClick}
          handleRemoveClick={handleRemoveClick}
          isSessionFinalized={isSessionFinalized}
          isSessionFull={isSessionFull}
          onMoveToTop={onMoveToTop}
          onPositionChange={onPositionChange}
          rosterFilter={rosterFilter}
          waitlist={waitlist}
          waitlistPositions={waitlistPositions}
        />
      )}

      {showCancelled && cancelledUsers.length > 0 && (
        <div>
          {rosterFilter === "all" && (
            <h3 className="text-base font-normal text-gray-900 dark:text-white mb-3">
              Cancelled ({cancelledUsers.length})
            </h3>
          )}
          {renderCompanySection(
            cancelledGrouped.groups,
            cancelledGrouped.ungrouped,
            "cancelled",
            (user: EventEnrollmentItemResponseDTO) => (
              <CancelledPersonRow
                formatDate={formatDate}
                handleReinstateClick={handleReinstateClick}
                isSessionFinalized={isSessionFinalized}
                user={user}
              />
            ),
            (user) => user.id,
          )}
        </div>
      )}

      {activeUsers.length === 0 &&
        waitlist.length === 0 &&
        cancelledUsers.length === 0 &&
        (loading ? (
          <div className="py-12 flex justify-center">
            <LoadingIndicator />
          </div>
        ) : (
          <div className="py-12 text-center text-gray-500 dark:text-gray-400">
            No enrollments found.
          </div>
        ))}
    </div>
  );
}
